/**
 * @file 作っている途中の答えを出す枠である。
 *
 * @what: 届いた文字をそのまま出し、まだ続くことを1行で伝える。書式 (見出し・数式) は付けない。
 * @why: 途中の Markdown や数式は閉じていないので、書式を付けると形が崩れたり消えたりする。
 *   できあがったら記録の面に切り替わり、そちらで書式の付いた形になる。
 * @failure: 例外を投げない (文字を出すだけ)。
 */

import { useEffect, useRef } from "react";
import "./study.css";

/** 下端とみなす余白である (この範囲にいれば、増えた分を追う)。 */
const STICK_MARGIN_PX = 48;

/** その要素を包む、動かせる面を返す (無ければ null)。 */
function scrollParentOf(el: HTMLElement | null): HTMLElement | null {
  for (let node = el?.parentElement ?? null; node !== null; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowY;
    if (overflow === "auto" || overflow === "scroll") return node;
  }
  return null;
}

export function AskStreamView({ text }: { text: string }) {
  const endRef = useRef<HTMLDivElement | null>(null);
  // 増えた分が見えるように、下端を追う。ただし読み返そうとして上へ動かしているときは追わない。
  // ※なぜなら数秒ごとに一番下へ引き戻されると、途中を読めなくなるためである。
  useEffect(() => {
    const end = endRef.current;
    if (!end || typeof end.scrollIntoView !== "function") return;
    const pane = scrollParentOf(end);
    if (pane !== null) {
      const atBottom = pane.scrollTop + pane.clientHeight >= pane.scrollHeight - STICK_MARGIN_PX;
      if (!atBottom) return;
    }
    end.scrollIntoView({ block: "end" });
  }, [text]);

  return (
    <div className="study-ask-stream">
      <p className="study-ask-stream-note" role="status">
        {text === "" ? "答えを作っています…" : "答えを作っています（続きが出ます）"}
      </p>
      <div className="study-ask-stream-body" aria-live="polite" aria-atomic="false">
        {text}
        <span className="study-ask-stream-caret" aria-hidden="true" />
      </div>
      <div ref={endRef} />
    </div>
  );
}
